import { Box, Button, Link, Paper, Typography } from '@mui/material';
import { ComponentType, useContext, useEffect, useMemo, useState } from 'react';
import checkCircleFilledIcon from '../../../assets/check-circle-filled.svg';
import contestEndedTicketIcon from '../../../assets/contest-ended-ticket.svg';
import contestTicketIcon from '../../../assets/contest-ticket.svg';
import contestVacationBg from '../../../assets/contest-vacation-bg.jpg';
import contestWinnerIcon from '../../../assets/contest-winner.svg';
import { AppContext } from '../../../context/AppContext';
import { ContestStatus } from '../../../types/contest';
import { contestPanelStyles as styles } from '../styles/contestPanelStyles';

type ContestDisplayState =
  | 'before-start'
  | 'active-no-entry'
  | 'active-earned'
  | 'inactive';
type ContestVisualState = ContestDisplayState | 'ended' | 'winner';

function getContestDisplayState(status: ContestStatus): ContestVisualState {
  if (status.contestPhase === 'before-start') {
    return 'before-start';
  }

  if (status.contestPhase === 'ended') {
    return 'ended';
  }

  if (status.contestPhase === 'winner') {
    return 'winner';
  }

  if (!status.contestActive) {
    return 'inactive';
  }

  return status.todayEntryEarned ? 'active-earned' : 'active-no-entry';
}

function getCountdownParts(targetIso: string, now: number): string[] {
  const diffMs = Math.max(0, new Date(targetIso).getTime() - now);
  const totalSeconds = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [days, hours, minutes, seconds].map((value) =>
    String(value).padStart(2, '0')
  );
}

function formatContestDate(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    timeZone: 'America/Chicago',
  }).format(date);
}

function getSafeContestUrl(value: string | null): string | null {
  if (!value) {
    return null;
  }

  try {
    const parsedUrl = new URL(value);
    return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:'
      ? parsedUrl.toString()
      : null;
  } catch {
    return null;
  }
}

const renderStatusCard = (state: ContestVisualState, startsAt: Date) => {
  if (state === 'before-start') {
    return (
      <Paper
        elevation={0}
        sx={{ ...styles.statusCard, ...styles.statusCardInfo }}
        aria-label='Giveaway begins soon'
      >
        <Box sx={styles.statusIconWrapInfo}>
          <Box
            component='img'
            src={contestTicketIcon}
            alt=''
            sx={styles.statusIconImage}
          />
        </Box>
        <Box>
          <Typography variant='h6' sx={{ fontSize: '1rem', fontWeight: 700 }}>
            Earn entries starting on {formatContestDate(startsAt)}
          </Typography>
          <Typography variant='body2' sx={{ mt: 0.5, lineHeight: 1.45 }}>
            Come back on {formatContestDate(startsAt)} to start earning daily
            entries with Rheo AI Scribe.
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (state === 'active-earned') {
    return (
      <Paper
        elevation={0}
        sx={{
          ...styles.statusCard,
          ...styles.statusCardSuccess,
          ...styles.statusCardSuccessAligned,
        }}
        aria-label='Entry earned today'
      >
        <Box sx={styles.statusIconWrapSuccess}>
          <Box
            component='img'
            src={checkCircleFilledIcon}
            alt=''
            sx={styles.statusIconImage}
          />
        </Box>
        <Box>
          <Typography variant='h6' sx={{ fontSize: '1rem', fontWeight: 700 }}>
            Entry earned today!
          </Typography>
          <Typography
            variant='body2'
            sx={{ mt: 0.5, ...styles.statusTextSuccess }}
          >
            Come back tomorrow to earn another. The more days you scribe, the
            better chance of winning.
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (state === 'ended') {
    return (
      <Paper
        elevation={0}
        sx={{ ...styles.statusCard, ...styles.statusCardNeutral }}
        aria-label='Giveaway ended'
      >
        <Box sx={styles.statusIconWrapNeutral}>
          <Box
            component='img'
            src={contestEndedTicketIcon}
            alt=''
            sx={styles.statusIconImage}
          />
        </Box>
        <Box>
          <Typography variant='h6' sx={{ fontSize: '1rem', fontWeight: 700 }}>
            Giveaway closed
          </Typography>
          <Typography variant='body2' sx={{ mt: 0.5, lineHeight: 1.45 }}>
            Entries are now closed. The winner will be selected by random
            drawing and notified via email within five (5) business days of the
            drawing.
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (state === 'winner') {
    return (
      <Paper
        elevation={0}
        sx={{
          ...styles.statusCard,
          ...styles.statusCardSuccess,
          ...styles.statusCardSuccessAligned,
        }}
        aria-label='Giveaway winner'
      >
        <Box sx={styles.statusIconWrapSuccess}>
          <Box
            component='img'
            src={contestWinnerIcon}
            alt=''
            sx={styles.statusWinnerIconImage}
          />
        </Box>
        <Box>
          <Typography variant='h6' sx={{ fontSize: '1rem', fontWeight: 700 }}>
            Congrats! You've won the giveaway.
          </Typography>
          <Typography
            variant='body2'
            sx={{ mt: 0.5, ...styles.statusTextSuccess }}
          >
            Check your inbox - instructions to claim your $5,000 travel gift
            card is on the way.
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{ ...styles.statusCard, ...styles.statusCardInfo }}
      aria-label='No giveaway entry yet today'
    >
      <Box sx={styles.statusIconWrapInfo}>
        <Box
          component='img'
          src={contestTicketIcon}
          alt=''
          sx={styles.statusIconImage}
        />
      </Box>
      <Box>
        <Typography variant='h6' sx={{ fontSize: '1rem', fontWeight: 700 }}>
          You have not made an entry today!
        </Typography>
        <Typography variant='body2' sx={{ mt: 0.5, lineHeight: 1.45 }}>
          Use AI Scribe with a patient and save it to their chart to earn
          today's entry.
        </Typography>
      </Box>
    </Paper>
  );
};

const renderEntriesCard = (
  state: ContestVisualState,
  totalEntries: number,
  endsAt: Date
) => {
  if (state === 'before-start') {
    return null;
  }

  return (
    <Box sx={styles.entriesCardWrap}>
      <Paper elevation={0} sx={styles.entriesCard}>
        <Box>
          <Typography sx={styles.entriesTitle}>Your Raffle Entries</Typography>
          <Typography component='div' sx={styles.entriesValue}>
            {totalEntries}
          </Typography>
          <Typography variant='body2' sx={styles.entriesCaption}>
            tickets in the drawing
          </Typography>
        </Box>

        <Box sx={styles.entriesMeta}>
          <Typography variant='body2' sx={styles.entriesMetaText}>
            Last day to enter:
          </Typography>
          <Typography variant='body2' sx={styles.entriesMetaDate}>
            {formatContestDate(endsAt)}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export const ContestPanel: ComponentType = () => {
  const { contestStatus, contestEntries } = useContext(AppContext);
  const [countdownNow, setCountdownNow] = useState(() => Date.now());
  const { entriesEarned, todayEntryEarned } = contestEntries;

  useEffect(() => {
    if (contestStatus.contestPhase !== 'before-start') {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setCountdownNow(Date.now());
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [contestStatus.contestPhase]);

  const displayState = useMemo(
    () =>
      getContestDisplayState({
        ...contestStatus,
        entriesEarned,
        todayEntryEarned,
      }),
    [contestStatus, entriesEarned, todayEntryEarned]
  );

  const contestStart = new Date(contestStatus.contestStart);
  const contestEnd = new Date(contestStatus.contestEnd);
  const shouldShowContestDynamicContent =
    contestStatus.configAvailable && displayState !== 'inactive';
  const contestDetailsURL = getSafeContestUrl(contestStatus.contestDetailsURL);
  const termsAndConditionsURL = getSafeContestUrl(
    contestStatus.termsAndConditionsURL
  );
  const countdownParts = useMemo(
    () => getCountdownParts(contestStatus.contestStart, countdownNow),
    [contestStatus.contestStart, countdownNow]
  );
  const heroBadge = shouldShowContestDynamicContent
    ? displayState === 'before-start'
      ? `Coming Soon - Starts ${formatContestDate(contestStart)}`
      : displayState === 'active-no-entry' || displayState === 'active-earned'
        ? `Ends ${formatContestDate(contestEnd)}`
        : `Ended ${formatContestDate(contestEnd)}`
    : null;

  return (
    <Box sx={styles.root}>
      <Box sx={styles.content}>
        <Paper
          elevation={0}
          sx={{
            ...styles.heroCard,
            minHeight: displayState === 'before-start' ? 230 : 180,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 0,
              backgroundImage: `url(${contestVacationBg})`,
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              transform: 'scale(1.01)',
            }}
          />
          {heroBadge ? (
            <Typography component='span' sx={styles.badge}>
              {heroBadge}
            </Typography>
          ) : null}

          <Box
            sx={{
              ...styles.heroContent,
              ...(displayState === 'before-start'
                ? styles.heroContentPreStart
                : styles.heroContentCompact),
            }}
          >
            <Typography component='h2' sx={styles.heroTitle}>
              $5,000 Vacation Giveaway
            </Typography>
            <Typography
              sx={{
                ...styles.heroDescription,
                ...(displayState === 'before-start'
                  ? null
                  : styles.heroDescriptionCompact),
              }}
            >
              {displayState === 'before-start'
                ? `Use AI Scribe to enter our dream vacation raffle. By using AI
              Scribe during the giveaway, you're opting in to the giveaway.`
                : `Earn a raffle entry each day you use AI Scribe. Up to 30
              entries for a chance to win a dream vacation for two.`}
            </Typography>

            {shouldShowContestDynamicContent &&
            displayState === 'before-start' ? (
              <>
                <Box sx={styles.countdownWrap} aria-label='Giveaway countdown'>
                  {[
                    ['Days', countdownParts[0]],
                    ['Hrs', countdownParts[1]],
                    ['Min', countdownParts[2]],
                    ['Sec', countdownParts[3]],
                  ].map(([label, value]) => (
                    <Box key={label} sx={styles.countdownCard}>
                      <Typography component='div' sx={styles.countdownValue}>
                        {value}
                      </Typography>
                      <Typography component='div' sx={styles.countdownLabel}>
                        {label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Typography component='div' sx={styles.countdownNote}>
                  *Countdown based on Central Time
                </Typography>
              </>
            ) : null}
          </Box>
        </Paper>

        {shouldShowContestDynamicContent
          ? renderStatusCard(displayState, contestStart)
          : null}

        {shouldShowContestDynamicContent
          ? renderEntriesCard(displayState, entriesEarned, contestEnd)
          : null}

        <Box component='hr' sx={styles.sectionDivider} />

        <Box>
          <Typography
            component='h3'
            sx={{ fontSize: '1.05rem', fontWeight: 800, mb: 2.25 }}
          >
            How to Win
          </Typography>

          <Box sx={styles.howToList}>
            {[
              'Use AI Scribe to transcribe a patient visit',
              'Save the SOAP note to the chart via Replace or Append.',
              statefulHowToCopy(
                displayState,
                contestStatus.maxPossibleEntries,
                shouldShowContestDynamicContent
              ),
            ].map((text, index) => (
              <Box key={text} sx={styles.howToItem}>
                <Box sx={styles.howToBadge}>{index + 1}</Box>
                <Typography variant='body2' sx={styles.howToText}>
                  {text}
                </Typography>
              </Box>
            ))}
          </Box>

          <Typography variant='body2' sx={styles.helperText}>
            Scribing multiple patients on the same day still counts as a single
            entry. Return each day to maximize your chances.
          </Typography>
        </Box>

        {shouldShowContestDynamicContent && contestDetailsURL ? (
          <Button
            component='a'
            href={contestDetailsURL}
            target='_blank'
            rel='noopener noreferrer'
            variant='outlined'
            fullWidth
            sx={styles.secondaryActionButton}
          >
            See Giveaway Details
          </Button>
        ) : null}

        {shouldShowContestDynamicContent &&
        (contestStatus.disclaimerMessage || termsAndConditionsURL) ? (
          <Typography variant='caption' sx={styles.footerText}>
            {contestStatus.disclaimerMessage}
            {termsAndConditionsURL ? (
              <>
                {' '}
                <Link
                  href={termsAndConditionsURL}
                  target='_blank'
                  rel='noopener noreferrer'
                  sx={styles.footerLink}
                >
                  Terms & Conditions
                </Link>
              </>
            ) : null}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

function statefulHowToCopy(
  state: ContestVisualState,
  maxPossibleEntries: number,
  configAvailable: boolean
) {
  if (!configAvailable) {
    return 'One entry is added per provider per calendar day during the giveaway period.';
  }

  if (state === 'before-start') {
    return `One entry added per provider per calendar day, up to ${maxPossibleEntries}.`;
  }

  return `One entry added per provider per calendar day, up to ${maxPossibleEntries}.`;
}
